import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import ctaClassroomImg from "@/assets/cta-classroom.jpg";

const EASE = [0.32, 0.72, 0, 1] as const;

export function LandingCTA() {
  const { demoSignIn } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const handleDemo = async () => {
    setLoading(true);
    try {
      await demoSignIn("admin");
      navigate({ to: "/admin" });
    } catch {
      toast.error("Không thể tải demo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 px-4 bg-white dark:bg-slate-900">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7, ease: EASE }}
        className="max-w-4xl mx-auto p-1.5 rounded-[2.5rem] bg-slate-900/[0.04] ring-1 ring-black/[0.04] dark:ring-white/10"
      >
        <div className="relative rounded-[calc(2.5rem-6px)] bg-slate-900 px-8 py-16 md:py-20 text-center overflow-hidden">
          {/* Background classroom image */}
          <img
            src={ctaClassroomImg}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark overlay for contrast */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-blue-900/85" />
          {/* Diffused glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-blue-500/25 blur-[120px] pointer-events-none" />

          <div className="relative">
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.1]">
              Sẵn sàng quản lý trung tâm
              <br />
              hiệu quả hơn?
            </h2>
            <p className="text-slate-400 dark:text-slate-500 mt-5 max-w-md mx-auto text-[15px] leading-relaxed">
              Bắt đầu miễn phí. Nâng cấp khi cần. Không hợp đồng ràng buộc.
            </p>

            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link
                to="/signup"
                className="group flex items-center gap-2 h-12 pl-6 pr-2 rounded-full bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-medium hover:bg-slate-100 dark:bg-slate-800 active:scale-[0.98] transition-all duration-500"
                style={{ transitionTimingFunction: "cubic-bezier(0.32,0.72,0,1)" }}
              >
                Đăng ký miễn phí
                <span className="size-8 rounded-full bg-slate-900/10 grid place-items-center group-hover:translate-x-0.5 transition-transform duration-500">
                  <ArrowRight className="size-4" strokeWidth={1.5} />
                </span>
              </Link>
              <button
                onClick={handleDemo}
                disabled={loading}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-white/70 font-medium ring-1 ring-white/20 hover:ring-white/40 hover:text-white transition-all duration-500 disabled:opacity-60"
              >
                {loading ? <Loader2 className="size-4 animate-spin" strokeWidth={1.5} /> : null}
                Xem demo
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
